import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  AnimalType,
  MedType,
  IndicationType,
  WeightUnit,
  DosageCalculation
} from '../types/dosage-types';
import { DosageCalculatorService } from '../services/dosage-calculator';
import AppAnalytics from '../analytics/AppAnalytics';

@customElement('dosage-calculator')
export class DosageCalculator extends LitElement {
  @property({ type: String }) animalType: AnimalType = AnimalType.DOG;
  @property({ type: String }) medType: MedType = MedType.TOLTRAZURIL;
  @property({ type: String }) indicationType: IndicationType = IndicationType.TREATMENT;
  @property({ type: Boolean }) readonly = false;

  @state() private weight = '';
  @state() private weightUnit: WeightUnit = WeightUnit.KG;
  @state() private calculation: DosageCalculation | null = null;
  @state() private showResults = false;

  // Use light DOM for Tailwind CSS
  createRenderRoot() {
    return this;
  }

  private handleWeightChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.weight = input.value;
    this.showResults = false;
    this.requestUpdate();
  }

  private handleWeightUnitChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    this.weightUnit = select.value as WeightUnit;
    if (this.calculation) {
      this.calculateDosage();
      this.requestUpdate();
    }
  }

  private calculateDosage() {
    const weightNum = parseFloat(this.weight);
    if (isNaN(weightNum) || weightNum <= 0) {
      this.calculation = null;
      this.showResults = false;
      this.requestUpdate();
      return;
    }

    this.calculation = DosageCalculatorService.calculate(
      this.animalType,
      this.medType,
      this.indicationType,
      weightNum,
      this.weightUnit
    );
    this.showResults = true;

    // Track dosage calculation
    AppAnalytics.getPlausible().trackEvent('Dosage Calculated', {
      props: {
        animal: this.animalType,
        medication: this.medType,
        indication: this.indicationType,
        weightUnit: this.weightUnit,
        isValid: this.calculation.isValid ? 'true' : 'false'
      }
    });

    this.requestUpdate();
  }

  private getAnimalName(animalType: AnimalType): string {
    const names = {
      [AnimalType.DOG]: 'Dog',
      [AnimalType.CAT]: 'Cat',
      [AnimalType.CHICKEN]: 'Chicken',
      [AnimalType.GOAT]: 'Goat',
      [AnimalType.HORSE]: 'Horse',
      [AnimalType.RABBIT]: 'Rabbit',
      [AnimalType.SHEEP]: 'Sheep',
      [AnimalType.PIG]: 'Pig',
      [AnimalType.CATTLE]: 'Cattle'
    };
    return names[animalType] || animalType;
  }

  private getMedicationName(medType: MedType): string {
    const names = {
      [MedType.TOLTRAZURIL]: 'Toltrazuril',
      [MedType.SULFADIMETHOXINE]: 'Sulfadimethoxine (Albon)',
      [MedType.FENBENDAZOLE]: 'Fenbendazole',
      [MedType.OMEPRAZOLE]: 'Omeprazole',
      [MedType.IVERMECTIN]: 'Ivermectin',
      [MedType.ALBENDAZOLE]: 'Albendazole',
      [MedType.METRONIDAZOLE]: 'Metronidazole',
      [MedType.AMPROLIUM]: 'Amprolium'
    };
    return names[medType] || medType;
  }

  private getIndicationName(indicationType: IndicationType): string {
    const names = {
      [IndicationType.TREATMENT]: 'Treatment',
      [IndicationType.PREVENTION]: 'Prevention',
      [IndicationType.PROPHYLAXIS]: 'Prophylaxis'
    };
    return names[indicationType] || indicationType;
  }

  private renderCalculatorHeader() {
    return html`
      <div class="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl mb-6 border border-blue-100">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">
          ${this.getMedicationName(this.medType)} Dosage Calculator
        </h2>
        <div class="text-gray-600 space-y-1">
          <p><strong>Animal:</strong> ${this.getAnimalName(this.animalType)}</p>
          <p><strong>Indication:</strong> ${this.getIndicationName(this.indicationType)}</p>
        </div>
      </div>
    `;
  }

  private renderWeightInput() {
    return html`
      <div class="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Enter Animal Weight</h3>

        <div class="flex gap-4 items-end">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">Weight</label>
            <input
              type="number"
              step="0.1"
              min="0"
              .value=${this.weight}
              @input=${this.handleWeightChange}
              @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.calculateDosage()}
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
              placeholder="Enter weight..."
              ?readonly=${this.readonly}
            />
          </div>

          <div class="w-24">
            <label class="block text-sm font-medium text-gray-700 mb-2">Unit</label>
            <select
              .value=${this.weightUnit}
              @change=${this.handleWeightUnitChange}
              class="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              ?disabled=${this.readonly}
            >
              <option value=${WeightUnit.KG}>kg</option>
              <option value=${WeightUnit.LBS}>lbs</option>
              <option value=${WeightUnit.GRAMS}>g</option>
            </select>
          </div>

          <button
            @click=${this.calculateDosage}
            ?disabled=${!this.weight || this.readonly}
            class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            Calculate
          </button>
        </div>
      </div>
    `;
  }

  private renderDosageResults() {
    if (!this.calculation || !this.showResults) return '';

    if (!this.calculation.isValid) {
      return html`
        <div class="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <h3 class="text-lg font-semibold text-red-800 mb-3">❌ Calculation Error</h3>
          <ul class="list-disc list-inside text-red-700 space-y-1">
            ${this.calculation.errors.map(error => html`<li>${error}</li>`)}
          </ul>
        </div>
      `;
    }

    return html`
      <div class="space-y-6">
        <!-- Dosage Results -->
        <div class="bg-green-50 border border-green-200 rounded-xl p-6">
          <h3 class="text-lg font-semibold text-green-800 mb-4 flex items-center">
            <span class="text-2xl mr-2">💊</span>
            Calculated Dosage
          </h3>

          <div class="grid md:grid-cols-2 gap-6">
            <!-- Loading Dose (if applicable) -->
            ${this.calculation.loadingDoseMg ? html`
              <div class="bg-white rounded-lg p-4 border border-green-100">
                <h4 class="font-semibold text-gray-900 mb-3">Loading Dose (Day 1)</h4>
                <div class="space-y-2">
                  <p><strong>Amount:</strong> ${DosageCalculatorService.formatDose(this.calculation.loadingDoseMg)} mg</p>
                  ${this.calculation.loadingDoseMl ? html`
                    <p><strong>Volume:</strong> ${DosageCalculatorService.formatDose(this.calculation.loadingDoseMl)} ml</p>
                  ` : ''}
                  ${this.calculation.loadingDoseTablets ? html`
                    <p><strong>Tablets:</strong> ${DosageCalculatorService.formatDose(this.calculation.loadingDoseTablets)} tablets</p>
                  ` : ''}
                </div>
              </div>
            ` : ''}

            <!-- Regular Dose -->
            <div class="bg-white rounded-lg p-4 border border-green-100">
              <h4 class="font-semibold text-gray-900 mb-3">
                ${this.calculation.loadingDoseMg ? 'Regular Dose (Day 2+)' : 'Dose'}
              </h4>
              <div class="space-y-2">
                <p><strong>Amount:</strong> ${DosageCalculatorService.formatDose(this.calculation.regularDoseMg)} mg</p>
                ${this.calculation.regularDoseMl ? html`
                  <p><strong>Volume:</strong> ${DosageCalculatorService.formatDose(this.calculation.regularDoseMl)} ml</p>
                ` : ''}
                ${this.calculation.regularDoseTablets ? html`
                  <p><strong>Tablets:</strong> ${DosageCalculatorService.formatDose(this.calculation.regularDoseTablets)} tablets</p>
                ` : ''}
              </div>
            </div>
          </div>
        </div>

        <!-- Treatment Schedule -->
        <div class="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 class="text-lg font-semibold text-blue-800 mb-4 flex items-center">
            <span class="text-2xl mr-2">📅</span>
            Treatment Schedule
          </h3>

          <div class="grid md:grid-cols-2 gap-6">
            <div class="space-y-3">
              <p><strong>Frequency:</strong> ${this.calculation.frequencyDescription}</p>
              <p><strong>Total Doses:</strong> ${this.calculation.totalDoses}</p>
              <p><strong>Regimen Type:</strong> ${this.calculation.regimen.regimenType === 'single' ? 'Single dose' : 'Multiple doses'}</p>
            </div>

            ${this.calculation.withdrawalDate ? html`
              <div class="bg-white rounded-lg p-4 border border-orange-200">
                <h4 class="font-semibold text-orange-800 mb-2">⚠️ Withdrawal Period</h4>
                <p class="text-sm text-orange-700">
                  Safe for consumption after: <br>
                  <strong>${this.calculation.withdrawalDate.toLocaleDateString()}</strong>
                </p>
              </div>
            ` : ''}
          </div>
        </div>

        <!-- Medication Information -->
        <div class="bg-gray-50 border border-gray-200 rounded-xl p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span class="text-2xl mr-2">ℹ️</span>
            Medication Information
          </h3>

          <div class="space-y-4">
            <div>
              <h4 class="font-semibold text-gray-900 mb-2">Available Formulations</h4>
              <ul class="list-disc list-inside text-gray-700 space-y-1">
                ${this.calculation.medication.commonFormulations.map(formulation => html`
                  <li>${formulation}</li>
                `)}
              </ul>
            </div>

            ${this.calculation.regimen.notes ? html`
              <div>
                <h4 class="font-semibold text-gray-900 mb-2">Administration Notes</h4>
                <p class="text-gray-700">${this.calculation.regimen.notes}</p>
              </div>
            ` : ''}
          </div>
        </div>

        <!-- Warnings -->
        ${this.calculation.warnings.length > 0 ? html`
          <div class="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <h3 class="text-lg font-semibold text-yellow-800 mb-4 flex items-center">
              <span class="text-2xl mr-2">⚠️</span>
              Important Warnings
            </h3>
            <ul class="list-disc list-inside text-yellow-700 space-y-1">
              ${this.calculation.warnings.map(warning => html`<li>${warning}</li>`)}
            </ul>
          </div>
        ` : ''}

        <!-- Disclaimer -->
        <div class="bg-gray-100 border border-gray-300 rounded-xl p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-3">📋 Important Disclaimer</h3>
          <p class="text-gray-700 text-sm leading-relaxed">
            This calculator provides dosage estimates based on established veterinary guidelines.
            Always consult with a qualified veterinarian before administering any medication.
            Dosages may need adjustment based on individual animal health status, concurrent medications,
            and specific clinical circumstances.
          </p>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="max-w-4xl mx-auto">
        ${this.renderCalculatorHeader()}
        ${this.renderWeightInput()}
        ${this.renderDosageResults()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dosage-calculator': DosageCalculator;
  }
}
